import {
	ref,
	onMounted,
	onUnmounted,
	computed,
	type Ref,
	watchEffect,
} from 'vue';

/**
 * Composable function that observes whether a given HTML element is overflowing its content.
 * @param elementRef A ref object containing the reference to the HTML element to observe.
 * @returns A computed boolean indicating whether the element is overflowing its content.
 */
export const useIsElementOverflowing = <T extends HTMLElement>(
	elementRef: Ref<T | null>
) => {
	const isOverflowing = ref(false);

	const checkOverflow = () => {
		const element = elementRef.value;

		if (!element) {
			return;
		}

		isOverflowing.value = element.offsetWidth < element.scrollWidth;
	};

	watchEffect(checkOverflow);

	onMounted(() => {
		window.addEventListener('resize', checkOverflow);
	});

	onUnmounted(() => {
		window.removeEventListener('resize', checkOverflow);
	});

	return computed(() => isOverflowing.value);
};
